import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { commitOrder as commitOrderBatch, SyncApiNetworkError, type CommitOrderItemInput } from "@cliqsuite/db/powersync";
import type { KitchenTicket, Member, Product, ProductVariant } from "@cliqsuite/db/schemas";
import { isShopOrder, shopCharge } from "@cliqsuite/db/schemas";
import { RECENT_ORDERS, RECENT_TICKETS, insertRow, updateRow, useLive, useRows, useSql } from "../lib/db";
import type { CartLine, CartLineSelection, HeldOrder, OrderType, PairedDevice, Screen, SignedInStaff, TenderType } from "../types";
import { queueSale } from "../lib/outbox";
import { openScanCamera } from "../lib/camera";
import { printKitchenTicket } from "../lib/print";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { useStoreConfig } from "../hooks/useStoreConfig";
import { useInstalledModules } from "../hooks/useInstalledModules";
import { useLoyaltyEarnRate, useEarnOnDiscounted, useRedeemRate } from "../hooks/useLoyaltyEarnRate";
import { usePosPreferences } from "../hooks/usePosPreferences";
import { useBarcodeScanner } from "../hooks/useBarcodeScanner";
import { formatMoney, nowClock, orderTypeLabel, startOfTodayIso } from "../lib/format";
import { nextTicketNo, readDrawer, readHeldOrders, writeDrawer, writeHeldOrders } from "../lib/local-store";
import { getNotificationStatus, playSound, requestNotificationPermission, subscribeToPush, unlockAudio } from "../lib/notify";
import { NavRail, MobileTopBar, NavDrawer, NAV_LABEL } from "./AppNav";
import { NotificationPanel, type AppNotification } from "./NotificationPanel";
import { MenuScreen } from "./MenuScreen";
import { CustomizerModal } from "./CustomizerModal";
import { LineAddOnsModal } from "./LineAddOnsModal";
import { RetailVariantModal } from "./RetailVariantModal";
import { CameraBarcodeModal } from "./CameraBarcodeModal";
import { CartPanel, MAX_OPEN_TICKETS } from "./CartPanel";

// Only one of these five is ever on screen at once (mutually exclusive with
// "home"/MenuScreen, which stays a static import as the default landing
// screen) — lazy so a till that never leaves the counter never pays for
// TablesScreen/KitchenScreen/etc.
// tables.css also holds the page head and line buttons every lazy screen uses, so it loads up front.
import "../tables.css";

const TablesScreen = lazy(() => import("./TablesScreen").then((m) => ({ default: m.TablesScreen })));
const BillsScreen = lazy(() => import("./BillsScreen").then((m) => ({ default: m.BillsScreen })));
const KitchenScreen = lazy(() => import("./KitchenScreen").then((m) => ({ default: m.KitchenScreen })));
const InventoryScreen = lazy(() => import("./InventoryScreen").then((m) => ({ default: m.InventoryScreen })));
const SettingsScreen = lazy(() => import("./SettingsScreen").then((m) => ({ default: m.SettingsScreen })));
import { PinApprovalModal } from "./PinApprovalModal";
import { ChargeScreen } from "./ChargeScreen";
import { DoneScreen } from "./DoneScreen";

export interface PosAppProps {
  device: PairedDevice;
  staff: SignedInStaff;
  /** Default clocks out too; `{ clockOut: false }` is "switch staff" (the shift stays open). */
  onSignOut: (options?: { clockOut?: boolean }) => void;
  onUnpairDevice: () => void;
  clockedIn: boolean;
  clockingIn: boolean;
  onClockIn: () => void;
  onClockOut: () => void;
  shiftError: string;
}

type CheckoutMode = { kind: "new" } | { kind: "settle"; orderId: string } | null;

/** Placeholder key so a "no selection yet" useLiveQuery filter never matches a real row. */
const NIL_ID = "00000000-0000-0000-0000-000000000000";

/** The signed-in till: menu → cart → checkout → real order write, plus tables/bills/kitchen/settings. */
/** Screens rebuilt on the canvas layout handle their own padding and scrolling. */
const OWN_LAYOUT = new Set<Screen>(["home", "tables", "kitchen", "bills", "inventory", "settings"]);

export function PosApp({ device, staff, onSignOut, onUnpairDevice, clockedIn, clockingIn, onClockIn, onClockOut, shiftError }: PosAppProps) {
  const isDesktop = useIsDesktop();
  const storeCfg = useStoreConfig(device.storeId);
  const currency = { code: storeCfg.currencyCode, precision: storeCfg.currencyPrecision };
  const installedModules = useInstalledModules(device.businessId);
  const hasRestaurant = installedModules.has("restaurant");
  const hasCrm = installedModules.has("crm");
  const hasRetail = installedModules.has("retail");
  const hasInventory = installedModules.has("inventory");
  const loyaltyEarnRate = useLoyaltyEarnRate(device.storeId);
  const earnOnDiscounted = useEarnOnDiscounted(device.storeId);
  const redeemRate = useRedeemRate(device.storeId);
  const { prefs, setToggle } = usePosPreferences();

  const [screen, setScreen] = useState<Screen>("home");
  const [navOpen, setNavOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  // A screen whose module is off is not shown and never loads; if the owner turns a module off while it is open, back to Sell.
  const screenOff = ((screen === "tables" || screen === "kitchen") && !hasRestaurant) || (screen === "inventory" && !hasInventory && !hasRestaurant);
  useEffect(() => {
    if (screenOff) setScreen("home");
  }, [screenOff]);

  // Re-subscribes to Web Push on every mount if permission was already
  // granted in an earlier session — safe to call repeatedly, and doesn't
  // need a gesture (only Notification.requestPermission() does). This is
  // what lets a "ready" alert land even with the PWA fully closed, not just
  // backgrounded (see sw.ts's push handler and services/sync-api's send
  // side). Deliberately does NOT call requestNotificationPermission() itself
  // — Chrome (and Safari) silently ignore a permission request that isn't
  // triggered by a real user gesture, so the very first request has to come
  // from the Push Notifications toggle's own tap (enableNotif below), not an
  // automatic effect on mount/preference-change.
  const [notifStatus, setNotifStatus] = useState(() => getNotificationStatus());
  useEffect(() => {
    if (prefs.notif && notifStatus === "granted") void subscribeToPush(device.deviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.deviceId]);

  // The Push Notifications toggle's onClick (only fired when turning it ON)
  // — this tap is what makes BOTH the permission prompt and the audio
  // unlock count as user-gesture-driven, required on mobile for each to
  // ever actually work. Mirrors Kitchen Display's enableNotifications.
  function enableNotif() {
    unlockAudio();
    void requestNotificationPermission().then((status) => {
      setNotifStatus(status);
      if (status === "granted") void subscribeToPush(device.deviceId);
    });
  }

  const [cart, setCart] = useState<CartLine[]>([]);
  const [employeeDiscountPercent, setEmployeeDiscountPercent] = useState(0);
  useEffect(() => {
    if (employeeDiscountPercent > 0 && (!storeCfg.employeeDiscountEnabled || employeeDiscountPercent !== storeCfg.employeeDiscountPercent)) {
      setEmployeeDiscountPercent(0);
    }
  }, [employeeDiscountPercent, storeCfg.employeeDiscountEnabled, storeCfg.employeeDiscountPercent]);
  const [orderType, setOrderType] = useState<OrderType>("counter");
  useEffect(() => {
    if (orderType !== "counter") setEmployeeDiscountPercent(0);
  }, [orderType]);
  useEffect(() => {
    if (orderType === "dine_in" && !hasRestaurant) setOrderType("counter");
  }, [orderType, hasRestaurant]);
  const [tableId, setTableId] = useState<string | null>(null);
  const [pickupName, setPickupName] = useState("");
  /** Whole-order free-text note (delivery instructions, "bring a candle",
   * ...) — distinct from a CartLine's own per-item note. Reset alongside
   * cart/orderType/tableId/pickupName after every successful charge/send. */
  const [orderNote, setOrderNote] = useState("");
  const [addOnsKey, setAddOnsKey] = useState<string | null>(null);
  // Raw while typing (trimmed when the order is built).
  function setLineNote(key: string, note: string) {
    setCart((prev) => prev.map((l) => (l.key === key ? { ...l, notes: note || undefined } : l)));
  }
  function removeLine(key: string) {
    setCart((prev) => prev.filter((l) => l.key !== key));
  }
  function setLineAddOns(key: string, addOns: CartLineSelection[]) {
    setCart((prev) => prev.map((line) => {
      if (line.key !== key) return line;
      const variants = (line.selections ?? []).filter((s) => !s.kind || s.kind === "variant");
      const oldAddOnTotal = (line.selections ?? []).filter((s) => s.kind && s.kind !== "variant").reduce((sum, s) => sum + s.priceDelta * (s.quantity ?? 1), 0);
      const newAddOnTotal = addOns.reduce((sum, s) => sum + s.priceDelta * (s.quantity ?? 1), 0);
      return { ...line, unitPrice: line.unitPrice - oldAddOnTotal + newAddOnTotal, selections: [...variants, ...addOns] };
    }));
  }

  // Open tickets: the active one lives in cart/orderType/... state; parked ones in `held`
  // (persisted on this device). Tabs keep their order when switching; staff can drag them.
  const [held, setHeld] = useState<HeldOrder[]>(() => readHeldOrders().slice(0, MAX_OPEN_TICKETS - 1));
  const [activeTicketId, setActiveTicketId] = useState<string>(() => crypto.randomUUID());
  // Numbered when its first item goes on, so reloads and empty tickets don't use up numbers.
  const [activeTicketNo, setActiveTicketNo] = useState("");
  const [ticketStartedAt, setTicketStartedAt] = useState(() => nowClock());
  const [tabOrder, setTabOrder] = useState<string[]>(() => [...readHeldOrders().slice(0, MAX_OPEN_TICKETS - 1).map((h) => h.id), activeTicketId]);
  useEffect(() => {
    if (cart.length > 0 && !activeTicketNo) setActiveTicketNo(nextTicketNo());
  }, [cart.length, activeTicketNo]);
  const [lastSale, setLastSale] = useState<{ label: string; cart: CartLine[] } | null>(null);
  const [drawer, setDrawer] = useState(() => readDrawer());

  // Offline-write outbox (lib/outbox.ts) — a checkout that fails to reach
  // sync-api at all (genuinely offline, not a real rejection) queues here
  // instead of erroring, so a power/connectivity outage doesn't block
  // ringing up a sale.
  const pendingSalesResult = useSql<{ n: number }>("SELECT COUNT(*) AS n FROM pending_sales WHERE needs_attention = 0");
  const addOnsResult = useRows("add_ons", "SELECT * FROM add_ons WHERE store_id = $1 AND is_active = true ORDER BY sort_order, name", [device.storeId]);
  const productsResult = useRows("products", "SELECT * FROM products WHERE store_id = $1 AND deleted_at IS NULL", [device.storeId]);
  const productCategoryResult = productsResult;
  const variantsResult = useRows("product_variants", "SELECT * FROM product_variants WHERE is_active = true");
  // Stock counts and tables are only read when their module is on (the query still runs, on nothing).
  const retailInventoryResult = useRows("store_inventory", hasInventory || hasRestaurant ? "SELECT * FROM store_inventory WHERE store_id = $1" : "SELECT * FROM store_inventory WHERE 0 AND store_id = $1", [device.storeId]);
  const isRetailLine = (line: CartLine) => line.isInventoryItem ?? !!productsResult?.rows.find((product) => product.id === line.productId)?.is_inventory_item;
  const pendingSalesCount = pendingSalesResult?.rows[0]?.n ?? 0;
  const attentionSalesResult = useSql<{ n: number }>("SELECT COUNT(*) AS n FROM pending_sales WHERE needs_attention = 1");
  const attentionSalesCount = attentionSalesResult?.rows[0]?.n ?? 0;

  const [checkout, setCheckout] = useState<CheckoutMode>(null);
  // The charge screen covers every tab, so leaving by the nav has to close it too (the ticket is
  // kept); otherwise the nav highlight moved while the charge screen stayed up.
  const navigate = (next: Parameters<typeof setScreen>[0]) => {
    setCheckout(null);
    setScreen(next);
  };
  const [success, setSuccess] = useState<{
    orderId: string;
    orderNo: string;
    total: number;
    tenderLabel: string;
    earnedPoints: number;
    memberName: string | null;
    pointsRedeemed?: number;
    receiptOnScreen?: boolean;
    pendingSync?: boolean;
  } | null>(null);
  const [sentInfo, setSentInfo] = useState<{ destLabel: string; pendingSync?: boolean } | null>(null);
  // Guards "Send to Kitchen" against a double-tap or a retry-after-a-slow-
  // response firing two concurrent commits — see sendToKitchen's own
  // comment for the real production bug this closes.
  const [sending, setSending] = useState(false);
  const [approval, setApproval] = useState<{ label: string; onApprove: () => void } | null>(null);
  const [customizing, setCustomizing] = useState<Product | null>(null);
  // Leaving Sell closes the item panel (it belongs to the menu, not the app).
  useEffect(() => setCustomizing(null), [screen]);
  const [retailVariantProduct, setRetailVariantProduct] = useState<Product | null>(null);
  const [cameraScanner, setCameraScanner] = useState<{ stream: MediaStream | null; error?: string } | null>(null);
  const [cameraRequesting, setCameraRequesting] = useState(false);

  // A connection drop mid-commit (WiFi hiccup, device sleep, tab closing)
  // can leave a request that actually committed server-side but never got
  // its response back — the UI then shows a generic error and lets staff
  // retry. Without reusing the exact same order/ticket/item ids across that
  // retry, sync-api's own dedup (routes/sales.ts's /commit) has nothing to
  // key off and a retry creates a genuine duplicate order. Built lazily on
  // first call, reused on every subsequent call, and only cleared when this
  // really is a new attempt (a fresh checkout, or the cart itself changed).
  const chargeAttemptRef = useRef<{ orderId: string; ticket: KitchenTicket | null; itemRows: CommitOrderItemInput[] } | null>(null);
  useEffect(() => {
    chargeAttemptRef.current = null;
  }, [checkout]);
  const sendAttemptRef = useRef<{ orderId: string; ticket: KitchenTicket | null; ticketId: string | null; itemRows: CommitOrderItemInput[] } | null>(null);
  useEffect(() => {
    sendAttemptRef.current = null;
  }, [cart]);

  const { data: storeOrders } = useLive("orders", RECENT_ORDERS, [device.storeId]);
  const { data: storeKitchenTickets } = useLive("kitchen_tickets", RECENT_TICKETS, [device.storeId]);
  const tablesResult = useRows("tables", hasRestaurant ? "SELECT * FROM tables WHERE store_id = $1" : "SELECT * FROM tables WHERE 0 AND store_id = $1", [device.storeId]);
  const tables = tablesResult?.rows;
  const { data: tableSessions } = useLive("table_sessions", "SELECT * FROM table_sessions WHERE store_id = $1", [device.storeId]);

  // Lives here (not inside KitchenScreen) so it keeps watching even while
  // the till is sitting on Menu/Bills/Settings — KitchenScreen only mounts
  // while its tab is open, so tracking "was it ready last render" there
  // reset to null (and missed the transition) every time staff navigated
  // away and back.
  const [justReadyIds, setJustReadyIds] = useState<Set<string>>(new Set());
  const prevTicketStatusRef = useRef<Map<string, string> | null>(null);

  // In-app notification list (bell icon in NavRail/MobileTopBar) — mirrors
  // the mockup's own notification panel, which never made it into the real
  // app. Session-only (resets on reload), same as the mockup's own state.
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const dismissNotification = (id: string) => setNotifications((prev) => prev.filter((n) => n.id !== id));

  // A guest ordered from outside the store, or sent a new payment screenshot: tell the staff, and
  // take them to the bill. Orders already there when the register opens are not announced.
  const seenShopOrdersRef = useRef<Map<string, string | null> | null>(null);
  const [billFocus, setBillFocus] = useState<string | null>(null);
  useEffect(() => {
    if (!storeOrders) return;
    const current = new Map(storeOrders.filter((o) => isShopOrder(o) && o.status === "open").map((o) => [o.id, o.payment_review]));
    const seen = seenShopOrdersRef.current;
    seenShopOrdersRef.current = current;
    if (seen === null) return;
    const fresh = storeOrders.filter((o) => current.has(o.id) && (!seen.has(o.id) || (o.payment_review === "pending" && seen.get(o.id) === "rejected")));
    if (fresh.length === 0) return;
    if (prefs.notif) playSound("/sounds/ticket-ready.mp3");
    setNotifications((prev) => [
      ...fresh.map((o) => ({
        id: `shop-${o.id}-${Date.now()}`,
        title: `Online order #${o.order_number}`,
        sub: o.payment_review === "pending" ? "Check the payment screenshot" : `${o.order_type === "online" ? "Delivery" : "Pick-up"}, ${o.payment_method_name ?? "cash"}`,
        time: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
        icon: "local_shipping",
        color: "#DC2D49",
        bg: "#FFF1F4",
        onOpen: () => {
          setBillFocus(o.id);
          setScreen("bills");
          setNotifOpen(false);
        },
      })),
      ...prev,
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeOrders]);
  const clearAllNotifications = () => setNotifications([]);

  useEffect(() => {
    const current = new Map<string, string>();
    (storeKitchenTickets ?? []).forEach((t) => {
      if (t.status !== "done") current.set(t.id, t.status);
    });
    const prev = prevTicketStatusRef.current;
    prevTicketStatusRef.current = current;
    if (prev === null) return; // first load — don't treat already-ready tickets as new transitions

    const readyIds: string[] = [];
    current.forEach((status, id) => {
      const prevStatus = prev.get(id);
      if (status === "ready" && prevStatus !== undefined && prevStatus !== "ready") readyIds.push(id);
    });
    if (readyIds.length === 0) return;

    // Push Notifications is the single toggle for this whole alert now —
    // in-app chime and the OS-level push (see the effect above) both live
    // behind it, not two separate preferences.
    if (prefs.notif) playSound("/sounds/ticket-ready.mp3");
    // The real OS notification for this same "ready" transition now comes
    // from sync-api's Web Push send (see routes/sales.ts's afterUpdate
    // hook) — calling notifyIfHidden here too would double-fire it.
    setJustReadyIds((prevSet) => {
      const next = new Set(prevSet);
      readyIds.forEach((id) => next.add(id));
      return next;
    });
    setTimeout(() => {
      setJustReadyIds((prevSet) => {
        const next = new Set(prevSet);
        readyIds.forEach((id) => next.delete(id));
        return next;
      });
    }, 8000);

    const newNotifs: AppNotification[] = readyIds.map((id) => {
      const t = (storeKitchenTickets ?? []).find((x) => x.id === id);
      const o = t ? (storeOrders ?? []).find((x) => x.id === t.order_id) : null;
      const table = o?.table_id ? (tables ?? []).find((x) => x.id === o.table_id) : null;
      const label =
        o?.order_type === "dine_in" ? (table ? (table.label ?? `Table ${String(table.table_number).padStart(2, "0")}`) : "Table") : "Pickup";
      return {
        id: `nr-${id}-${Date.now()}`,
        title: `Order ready · ${label}`,
        sub: o ? `#${o.order_number}` : "",
        time: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
        icon: "check_circle",
        color: "#21A35B",
        bg: "#E9F8EF",
      };
    });
    setNotifications((prevList) => [...newNotifs, ...prevList].slice(0, 30));
  }, [storeKitchenTickets, storeOrders, tables, prefs.notif]);

  // Guest Portal's "Call Staff" (staff_called_at going null→set) and
  // "Request Bill" (status transitioning into "bill_requested") — the table
  // card/drawer badge (TablesScreen.tsx) already shows either persistently
  // until acknowledged/settled, so there's no need for a separate transient
  // highlight here, just the notification list entry + a sound (previously
  // visual-only — reusing the kitchen-ready chime would've read as a
  // kitchen event, so this uses its own staff-alert.mp3 instead).
  const prevSessionStateRef = useRef<Map<string, { staffCalledAt: string | null; status: string }> | null>(null);
  useEffect(() => {
    const current = new Map<string, { staffCalledAt: string | null; status: string }>();
    (tableSessions ?? []).forEach((s) => {
      if (s.status !== "closed") current.set(s.id, { staffCalledAt: s.staff_called_at, status: s.status });
    });
    const prev = prevSessionStateRef.current;
    prevSessionStateRef.current = current;
    if (prev === null) return; // first load — don't treat an already-active call/bill as new

    const calledSessionIds: string[] = [];
    const billRequestedSessionIds: string[] = [];
    current.forEach((state, sessionId) => {
      const prevState = prev.get(sessionId);
      if (state.staffCalledAt && !prevState?.staffCalledAt) calledSessionIds.push(sessionId);
      if (state.status === "bill_requested" && prevState?.status !== "bill_requested") billRequestedSessionIds.push(sessionId);
    });
    if (calledSessionIds.length === 0 && billRequestedSessionIds.length === 0) return;

    if (prefs.notif) playSound("/sounds/staff-alert.mp3");
    // The real OS notification for these same transitions now comes from
    // sync-api's Web Push send (see routes/floor.ts's afterUpdate hook) —
    // this in-app list entry is only for while the app is actually open.
    const labelFor = (sessionId: string) => {
      const session = (tableSessions ?? []).find((s) => s.id === sessionId);
      const table = session?.table_id ? (tables ?? []).find((t) => t.id === session.table_id) : null;
      return table ? (table.label ?? `Table ${String(table.table_number).padStart(2, "0")}`) : "Table";
    };
    const newNotifs: AppNotification[] = [
      ...calledSessionIds.map((sessionId) => ({
        id: `sc-${sessionId}-${Date.now()}`,
        title: `Guest calling · ${labelFor(sessionId)}`,
        sub: "Needs assistance",
        time: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
        icon: "notifications_active",
        color: "#2D6FE0",
        bg: "#EAF1FD",
      })),
      ...billRequestedSessionIds.map((sessionId) => ({
        id: `br-${sessionId}-${Date.now()}`,
        title: `Bill requested · ${labelFor(sessionId)}`,
        sub: "Ready to settle",
        time: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
        icon: "receipt_long",
        color: "#C98A04",
        bg: "#FCF3D6",
      })),
    ];
    setNotifications((prevList) => [...newNotifs, ...prevList].slice(0, 30));
  }, [tableSessions, tables, prefs.notif]);

  const settleOrderId = checkout?.kind === "settle" ? checkout.orderId : null;
  const settleOrder = settleOrderId ? (storeOrders ?? []).find((o) => o.id === settleOrderId) ?? null : null;
  const { data: settleItems } = useLive("order_items", "SELECT * FROM order_items WHERE order_id = $1 AND is_voided = 0", [settleOrderId ?? NIL_ID]);
  const settleItemIds = useMemo(() => (settleItems ?? []).map((i) => i.id), [settleItems]);
  const { data: settleSelections } = useLive(
    "order_item_option_selections",
    "SELECT * FROM order_item_option_selections WHERE order_item_id IN (SELECT value FROM json_each($1))",
    [JSON.stringify(settleItemIds)],
  );
  const settleOptionIdsByItem = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const s of settleSelections ?? []) {
      if (!s.option_id) continue;
      const list = m.get(s.order_item_id) ?? [];
      list.push(s.option_id);
      m.set(s.order_item_id, list);
    }
    return m;
  }, [settleSelections]);
  const settleAddOnsByItem = useMemo(() => {
    const configured = new Map((addOnsResult?.rows ?? []).map((a) => [a.id, a]));
    const result = new Map<string, Array<{ ingredientId: string | null; ingredientQty: number; quantity: number }>>();
    for (const selection of settleSelections ?? []) {
      if (selection.selection_type !== "add_on" || !selection.add_on_id) continue;
      const addOn = configured.get(selection.add_on_id);
      if (!addOn) continue;
      const list = result.get(selection.order_item_id) ?? [];
      list.push({ ingredientId: addOn.ingredient_id, ingredientQty: addOn.ingredient_qty, quantity: selection.quantity });
      result.set(selection.order_item_id, list);
    }
    return result;
  }, [settleSelections, addOnsResult?.rows]);

  // Recipe stock deduction — every product's bill-of-materials (set in Owner
  // Portal's Menu drawer), used to decrement raw ingredient stock the moment
  // a sale is finalized (chargeNewSale / settleExistingOrder below). Not
  // triggered by sendToKitchen, since that's pre-payment and a dish can be
  // voided/changed before it's actually paid for. A recipe line with a
  // variant_option_id only counts if that option was actually picked on the
  // line being sold — base lines (variant_option_id null) always count.
  //
  // Only computes WHICH ingredients and how much — the actual write happens
  // server-side, inside the same atomic transaction as the order commit
  // (see routes/sales.ts's `/commit` handler and commitOrderBatch's
  // `stockDeductions` field). No longer needs to read the current ingredient
  // row client-side (that was only for the old optimistic `draft.quantity -
  // used` write) — the server applies `GREATEST(0, quantity - qty)` itself.
  const { data: recipeLines } = useLive("product_ingredients", "SELECT * FROM product_ingredients");
  function computeStockDeductions(items: Array<{ productId: string | null; qty: number; optionIds: string[]; addOns?: Array<{ ingredientId: string | null; ingredientQty: number; quantity: number }> }>): Array<{ ingredientId: string; qty: number }> {
    const linesByProduct = new Map<string, typeof recipeLines>();
    for (const line of recipeLines ?? []) {
      const list = linesByProduct.get(line.product_id) ?? [];
      list.push(line);
      linesByProduct.set(line.product_id, list);
    }
    const totals = new Map<string, number>();
    for (const item of items) {
      if (item.productId) {
        for (const line of linesByProduct.get(item.productId) ?? []) {
          if (line.variant_option_id && !item.optionIds.includes(line.variant_option_id)) continue;
          const used = line.qty * item.qty;
          totals.set(line.ingredient_id, (totals.get(line.ingredient_id) ?? 0) + used);
        }
      }
      for (const addOn of item.addOns ?? []) {
        if (!addOn.ingredientId || addOn.ingredientQty <= 0) continue;
        const used = addOn.ingredientQty * addOn.quantity * item.qty;
        totals.set(addOn.ingredientId, (totals.get(addOn.ingredientId) ?? 0) + used);
      }
    }
    return Array.from(totals.entries()).map(([ingredientId, qty]) => ({ ingredientId, qty }));
  }

  const cartQtyByProduct = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of cart) m.set(l.productId, (m.get(l.productId) ?? 0) + l.qty);
    return m;
  }, [cart]);

  const subtotal = cart.reduce((s, l) => s + l.unitPrice * l.qty, 0);
  const employeeDiscount = Math.min(subtotal, Math.round((subtotal * employeeDiscountPercent) / 100));
  const discountedSubtotal = subtotal - employeeDiscount;
  const rawTax = Math.round(subtotal * storeCfg.taxRate);
  const tax = Math.round(discountedSubtotal * storeCfg.taxRate);
  const total = discountedSubtotal + tax;
  const itemCount = cart.reduce((s, l) => s + l.qty, 0);
  const taxLabelFull = `Tax (${storeCfg.taxLabel} ${Math.round(storeCfg.taxRate * 100)}%)`;

  const selectedTable = tableId ? tables?.find((t) => t.id === tableId) ?? null : null;
  const selectedTableLabel = selectedTable ? selectedTable.label?.trim() || `Table ${String(selectedTable.table_number).padStart(2, "0")}` : "";
  const [scanNotice, setScanNotice] = useState<{ message: string; error: boolean } | null>(null);

  function showScanNotice(message: string, error = false) {
    setScanNotice({ message, error });
    window.setTimeout(() => setScanNotice((current) => current?.message === message ? null : current), 2400);
  }

  function addProduct(p: Product) {
    setCart((prev) => {
      const idx = prev.findIndex((l) => l.key === p.id);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = { ...next[idx]!, qty: next[idx]!.qty + 1 };
        return next;
      }
      return [...prev, { key: p.id, productId: p.id, variantId: null, isInventoryItem: p.is_inventory_item, name: p.name, imageUrl: p.image_url, unitPrice: p.base_price, qty: 1 }];
    });
  }
  /** Tapping a product either adds it straight to the cart or opens the customizer, matching the mockup's openCustomizer/addDirect split. */
  function handleProductTap(p: Product) {
    if (p.is_inventory_item && p.has_variants) setRetailVariantProduct(p);
    else if (p.is_inventory_item) addScannedProduct(p, null);
    else if (p.has_variants) setCustomizing(p);
    else addProduct(p);
  }
  function addConfiguredProduct(p: Product, qty: number, selections: CartLineSelection[], unitPrice: number, note?: string) {
    const signature = selections.map((s) => s.optionId ?? s.label).sort().join(",");
    // A note makes it its own line: "no ice" and a plain one aren't the same drink.
    const key = [p.id, signature, note].filter(Boolean).join("|");
    setCart((prev) => {
      const idx = prev.findIndex((l) => l.key === key);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = { ...next[idx]!, qty: next[idx]!.qty + qty };
        return next;
      }
      return [...prev, { key, productId: p.id, variantId: null, isInventoryItem: p.is_inventory_item, name: p.name, imageUrl: p.image_url, unitPrice, qty, selections, notes: note }];
    });
  }

  function addScannedProduct(product: Product, variant: ProductVariant | null) {
    if (!product.is_available) {
      showScanNotice(`${product.name} is not available for sale`, true);
      return;
    }
    const inventory = product.is_inventory_item
      ? (retailInventoryResult?.rows ?? []).find((row) => row.product_id === product.id && (row.variant_id ?? null) === (variant?.id ?? null))
      : null;
    const key = variant ? `${product.id}|retail:${variant.id}` : product.id;
    const inCart = cart.find((line) => line.key === key)?.qty ?? 0;
    if (product.is_inventory_item && (!inventory || inventory.is_out_of_stock || inventory.quantity <= inCart)) {
      showScanNotice(`${product.name}${variant ? ` · ${variant.variant_label}` : ""} is out of stock`, true);
      return;
    }
    setCart((previous) => {
      const index = previous.findIndex((line) => line.key === key);
      if (index >= 0) {
        const next = previous.slice();
        next[index] = { ...next[index]!, qty: next[index]!.qty + 1 };
        return next;
      }
      return [...previous, {
        key,
        productId: product.id,
        variantId: variant?.id ?? null,
        variantLabel: variant?.variant_label ?? null,
        isInventoryItem: product.is_inventory_item,
        name: variant ? `${product.name} · ${variant.variant_label}` : product.name,
        imageUrl: product.image_url,
        unitPrice: variant?.price_override ?? product.base_price,
        qty: 1,
      }];
    });
    showScanNotice(`Added ${product.name}${variant ? ` · ${variant.variant_label}` : ""}`);
  }

  function handleBarcodeScan(rawBarcode: string) {
    const barcode = rawBarcode.trim();
    const products = productsResult?.rows ?? [];
    const productMatches = products.filter((row) => row.barcode === barcode || row.sku === barcode);
    const variantMatches = (variantsResult?.rows ?? []).filter((row) => row.barcode === barcode || row.sku === barcode);
    if (productMatches.length + variantMatches.length > 1) {
      showScanNotice(`Barcode ${barcode} matches more than one item`, true);
      return;
    }
    const product = productMatches[0];
    const variant = variantMatches[0] ?? null;
    const variantProduct = variant ? products.find((row) => row.id === variant.product_id) ?? null : null;
    const matchedProduct = variantProduct ?? product ?? null;
    if (!matchedProduct) {
      showScanNotice(`Barcode ${barcode} was not found`, true);
      return;
    }
    if (matchedProduct.has_variants && !variant) {
      if (matchedProduct.is_inventory_item) setRetailVariantProduct(matchedProduct);
      else setCustomizing(matchedProduct);
      showScanNotice(`Choose a variant for ${matchedProduct.name}`);
      return;
    }
    addScannedProduct(matchedProduct, variant);
  }

  useBarcodeScanner(handleBarcodeScan, screen === "home" && checkout === null && !cameraScanner);

  async function openCameraScanner() {
    if (cameraRequesting || cameraScanner) return;
    setCameraRequesting(true);
    try {
      setCameraScanner(await openScanCamera());
    } finally {
      setCameraRequesting(false);
    }
  }

  function closeCameraScanner() {
    cameraScanner?.stream?.getTracks().forEach((track) => track.stop());
    setCameraScanner(null);
  }
  function inc(key: string) {
    const line = cart.find((item) => item.key === key);
    if (line && isRetailLine(line)) {
      const inventory = (retailInventoryResult?.rows ?? []).find((row) => row.product_id === line.productId && (row.variant_id ?? null) === (line.variantId ?? null));
      if (!inventory || inventory.is_out_of_stock || line.qty >= inventory.quantity) {
        showScanNotice(`${line.name} has no more stock available`, true);
        return;
      }
    }
    setCart((prev) => prev.map((l) => (l.key === key ? { ...l, qty: l.qty + 1 } : l)));
  }
  function dec(key: string) {
    setCart((prev) => prev.map((l) => (l.key === key ? { ...l, qty: l.qty - 1 } : l)).filter((l) => l.qty > 0));
  }
  /** After a sale or a send: the same tab starts a fresh ticket. */
  function finishTicket() {
    setActiveTicketNo("");
    setTicketStartedAt(nowClock());
  }
  function clearCart() {
    setCart([]);
    setOrderNote("");
    setEmployeeDiscountPercent(0);
  }

  function toggleEmployeeDiscount() {
    if (employeeDiscountPercent > 0) {
      setEmployeeDiscountPercent(0);
      return;
    }
    if (!storeCfg.employeeDiscountEnabled) return;
    setEmployeeDiscountPercent(storeCfg.employeeDiscountPercent);
  }

  function requestToggleAvailability(product: Product) {
    const willBe = product.is_available ? "Mark" : "Restore";
    setApproval({
      label: `${willBe} ${product.name} ${product.is_available ? "as 86 (sold out)" : "to the menu"}`,
      onApprove: () => {
        void updateRow("products", product.id, { is_available: !product.is_available, updated_at: new Date().toISOString() });
        setApproval(null);
      },
    });
  }

  function earnPointsFor(totalForEarn: number, discount: number) {
    if (discount > 0 && !earnOnDiscounted) return 0;
    return Math.floor((totalForEarn / 1000) * loyaltyEarnRate);
  }

  function saveHeld(next: HeldOrder[]) {
    setHeld(next);
    writeHeldOrders(next);
  }
  function snapshotActive(): HeldOrder | null {
    if (cart.length === 0) return null;
    return { id: activeTicketId, orderNo: activeTicketNo, heldAt: ticketStartedAt, cart, orderType, tableId, pickupName, orderNote };
  }
  function loadTicket(t: HeldOrder) {
    setActiveTicketId(t.id);
    setActiveTicketNo(t.orderNo);
    setTicketStartedAt(t.heldAt);
    setCart(t.cart);
    setOrderType(t.orderType);
    setTableId(t.tableId);
    setPickupName(t.pickupName);
    setOrderNote(t.orderNote);
    setEmployeeDiscountPercent(0);
  }
  function blankTicket(id: string): HeldOrder {
    return { id, orderNo: "", heldAt: nowClock(), cart: [], orderType: "counter", tableId: null, pickupName: "", orderNote: "" };
  }
  /** Switch tabs: the current ticket is parked (dropped if empty), the picked one opens in place. */
  function switchTicket(id: string) {
    if (id === activeTicketId) return;
    const target = held.find((h) => h.id === id);
    if (!target) return;
    const parked = snapshotActive();
    saveHeld([...held.filter((h) => h.id !== id), ...(parked ? [parked] : [])]);
    if (!parked) setTabOrder((order) => order.filter((x) => x !== activeTicketId));
    loadTicket(target);
  }
  /** "+" and Park: park this ticket and start a new one right after it. At most MAX_OPEN_TICKETS. */
  function newTicket() {
    const parked = snapshotActive();
    if (!parked || held.length + 1 >= MAX_OPEN_TICKETS) return;
    saveHeld([...held, parked]);
    const fresh = blankTicket(crypto.randomUUID());
    setTabOrder((order) => {
      const next = [...order];
      next.splice(order.indexOf(activeTicketId) + 1, 0, fresh.id);
      return next;
    });
    loadTicket(fresh);
  }
  function moveTab(id: string, toIndex: number) {
    setTabOrder((order) => {
      const next = order.filter((x) => x !== id);
      next.splice(toIndex, 0, id);
      return next;
    });
  }
  function ticketLabel(t: Pick<HeldOrder, "orderNo" | "orderType" | "tableId" | "pickupName">) {
    if (t.orderType === "dine_in" && t.tableId) {
      const table = tables?.find((x) => x.id === t.tableId);
      if (table) return table.label?.trim() || `Table ${String(table.table_number).padStart(2, "0")}`;
    }
    const no = t.orderNo ? `#${t.orderNo}` : "";
    if (t.orderType === "pickup") return `${no} ${t.pickupName.trim() || "Pickup"}`.trim();
    return no || "New ticket";
  }
  const ticketTabs = useMemo(() => {
    const byId = new Map(held.map((h) => [h.id, h]));
    const ids = [...tabOrder.filter((id) => id === activeTicketId || byId.has(id)), ...held.map((h) => h.id).filter((id) => !tabOrder.includes(id))];
    if (!ids.includes(activeTicketId)) ids.push(activeTicketId);
    return ids.map((id) => {
      const t = id === activeTicketId ? { orderNo: activeTicketNo, orderType, tableId, pickupName } : byId.get(id)!;
      return { id, label: ticketLabel(t), active: id === activeTicketId };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [held, tabOrder, activeTicketId, activeTicketNo, orderType, tableId, pickupName, tables]);
  function repeatLastSale() {
    if (lastSale && cart.length === 0) setCart(lastSale.cart.map((l) => ({ ...l })));
  }

  /** Cart lines -> the order_items (+ one order_item_option_selections row per chosen customizer option) commitOrderBatch expects. kitchenTicketId links these items to whichever round they belong to — null for an order with no kitchen workflow. */
  function buildOrderItemRows(orderId: string, lines: CartLine[], now: string, kitchenTicketId: string | null): CommitOrderItemInput[] {
    return lines.map((line) => {
      const itemId = crypto.randomUUID();
      return {
        row: {
          id: itemId,
          order_id: orderId,
          product_id: line.productId,
          variant_id: line.variantId ?? null,
          product_name: line.name,
          variant_label: line.variantLabel ?? (line.selections?.some((selection) => (selection.kind ?? "variant") === "variant")
            ? line.selections.filter((selection) => (selection.kind ?? "variant") === "variant").map((selection) => selection.label).join(", ")
            : null),
          unit_price: line.unitPrice,
          // Always overwritten server-side at commit time (sync-api computes
          // the real cost from current catalog data) — this client never
          // knows cost, but the field is required by OrderItem's inferred
          // type (z.default() only makes it optional on input, not output).
          unit_cost: 0,
          quantity: line.qty,
          line_total: line.unitPrice * line.qty,
          is_voided: false,
          notes: line.notes?.trim() || null,
          kitchen_ticket_id: isRetailLine(line) ? null : kitchenTicketId,
          created_at: now,
          updated_at: now,
        },
        selections: (line.selections ?? []).map((sel) => ({
          id: crypto.randomUUID(),
          order_item_id: itemId,
          option_id: sel.optionId,
          add_on_id: sel.addOnId ?? null,
          selection_type: sel.kind ?? "variant",
          quantity: sel.quantity ?? 1,
          option_label: sel.label,
          price_delta: sel.priceDelta,
          // Same as unit_cost above — always overwritten server-side.
          cost_delta: 0,
          created_at: now,
        })),
      };
    });
  }

  /** One row per kitchen "round" — see packages/db's kitchenTicketSchema doc. */
  function newKitchenTicket(orderId: string, now: string): KitchenTicket {
    return {
      id: crypto.randomUUID(),
      order_id: orderId,
      store_id: device.storeId,
      status: "new",
      done_at: null,
      nudged_at: null, started_at: null,
      created_at: now,
      updated_at: now,
    };
  }

  /** Most recent kitchen ticket for an order, if any — used to decide whether a new round of items joins it or needs a fresh ticket. */
  function latestTicketFor(orderId: string) {
    return (
      (storeKitchenTickets ?? [])
        .filter((t) => t.order_id === orderId)
        .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))[0] ?? null
    );
  }

  async function chargeNewSale(payload: {
    tenderType: TenderType;
    methodName: string;
    discount: number;
    member: Member | null;
    promoId: string | null;
    voucherId: string | null;
    pointsRedeemed?: number;
    receiptOnScreen?: boolean;
  }) {
    const now = new Date().toISOString();
    const discount = Math.min(payload.discount, subtotal);
    const discountSource = employeeDiscountPercent > 0
      ? "employee" as const
      : payload.promoId
        ? "promo" as const
        : payload.voucherId
          ? "voucher" as const
          : payload.pointsRedeemed
            ? "points" as const
            : "none" as const;
    const discountedSub = subtotal - discount;
    const tax = Math.round(discountedSub * storeCfg.taxRate);
    const total = discountedSub + tax;
    const earned = payload.member ? earnPointsFor(total, discount) : 0;

    // Built once per checkout attempt (see chargeAttemptRef's own doc) —
    // a retry after a dropped-connection error reuses the exact same ids
    // instead of generating a fresh order, so sync-api's own dedup logic
    // recognizes it as the same attempt rather than a brand-new sale.
    if (!chargeAttemptRef.current) {
      const orderId = crypto.randomUUID();
      const ticket = cart.some((line) => !isRetailLine(line)) ? newKitchenTicket(orderId, now) : null;
      chargeAttemptRef.current = { orderId, ticket, itemRows: buildOrderItemRows(orderId, cart, now, ticket?.id ?? null) };
    }
    const { orderId, ticket, itemRows } = chargeAttemptRef.current;

    // Stock/loyalty/voucher all land atomically inside the same server
    // transaction as the order itself now (routes/sales.ts's `/commit`
    // handler) — no more separate post-commit writes here, and no more
    // `!duplicate` gate needed: a retry replays the identical inputs into
    // an already-idempotent transaction.
    const commitInput = {
      order: {
        id: orderId,
        business_id: device.businessId,
        store_id: device.storeId,
        order_number: "AUTO",
        order_type: orderType,
        source: "pos" as const,
        table_id: orderType === "dine_in" ? tableId : null,
        customer_name: orderType === "pickup" ? pickupName.trim() || null : null,
        loyalty_member_id: payload.member?.id ?? null,
        staff_id: staff.id,
        device_id: device.deviceId,
        subtotal: subtotal,
        discount: discount,
        discount_source: discountSource,
        employee_discount_percent: employeeDiscountPercent > 0 ? employeeDiscountPercent : null,
        tax: tax,
        rounding_adjustment: 0,
        total: total,
        tender_type: payload.tenderType,
        payment_method_name: payload.methodName,
        payment_status: "paid" as const,
        promo_id: payload.promoId,
        voucher_id: payload.voucherId,
        points_earned: earned,
        points_redeemed: payload.pointsRedeemed ?? 0,
        status: "closed" as const,
        notes: orderNote.trim() || null,
        refund_reason: null,
        refunded_at: null,
        refunded_by_staff_id: null,
        completed_at: now,
        created_at: now,
        updated_at: now,
      },
      kitchenTicket: ticket ?? undefined,
      items: itemRows,
      stockDeductions: computeStockDeductions(
        cart.map((line) => ({
          productId: line.productId,
          qty: line.qty,
          optionIds: (line.selections ?? []).flatMap((s) => (s.optionId ? [s.optionId] : [])),
          addOns: (line.selections ?? []).filter((s) => s.kind === "add_on").map((s) => ({ ingredientId: s.ingredientId ?? null, ingredientQty: s.ingredientQty ?? 0, quantity: s.quantity ?? 1 })),
        })),
      ),
      voucherRedeem: payload.voucherId ? { voucherId: payload.voucherId } : undefined,
      pointsRedeem: payload.member && payload.pointsRedeemed ? { memberId: payload.member.id, points: payload.pointsRedeemed } : undefined,
      employeeDiscountPercent: employeeDiscountPercent > 0 ? employeeDiscountPercent : undefined,
      loyaltyEarn: payload.member && earned > 0 ? { memberId: payload.member.id, points: earned } : undefined,
    };

    // A genuine network failure (offline, connection refused) queues the
    // sale locally and proceeds as if it succeeded — the cashier isn't
    // blocked by an outage. A real rejection (a validation error, the
    // store/staff row gone, ...) is NOT caught here and propagates up to
    // ChargeScreen's own catch, same as before: retrying an unchanged
    // request that the server actually rejected can't succeed differently.
    let orderNumber = "—";
    let pendingSync = false;
    try {
      const { order: persistedOrder } = await commitOrderBatch(commitInput);
      orderNumber = persistedOrder?.order_number ?? "—";
    } catch (err) {
      if (!(err instanceof SyncApiNetworkError)) throw err;
      await queueSale(orderId, err.requestBody);
      pendingSync = true;
    }

    // Auto-print gated on the store's preference; skipped for a
    // pendingSync (offline-queued) sale — there's no persisted order_id yet
    // for the server to build a receipt payload from. The outbox's own
    // flushOne picks up printing for these once they actually sync (see
    // lib/outbox.ts).

    setSuccess({ orderId, orderNo: orderNumber, total: total, tenderLabel: payload.methodName, earnedPoints: earned, memberName: payload.member?.name ?? null, receiptOnScreen: payload.receiptOnScreen, pendingSync });
    setLastSale({ label: orderNumber === "—" ? "" : `#${orderNumber}`, cart });
    finishTicket();
    setCheckout(null);
    setCart([]);
    setEmployeeDiscountPercent(0);
    setTableId(null);
    setPickupName("");
    setOrderType("counter");
    setOrderNote("");
  }

  async function settleExistingOrder(payload: {
    tenderType: TenderType;
    methodName: string;
    discount: number;
    member: Member | null;
    promoId: string | null;
    voucherId: string | null;
    pointsRedeemed?: number;
    receiptOnScreen?: boolean;
  }) {
    if (!settleOrder) return;
    // Same rule TablesScreen's Charge button already enforces (and disables
    // for) — kept here too so this can't be bypassed by any other path into
    // settleExistingOrder. A ticket-less order (no kitchen workflow, e.g.
    // plain retail) always passes.
    const unreadyTicket = (storeKitchenTickets ?? []).find(
      (t) => t.order_id === settleOrder.id && t.status !== "ready" && t.status !== "done",
    );
    if (unreadyTicket) return;
    const now = new Date().toISOString();
    // An online order's price was set when the guest placed it: the register takes the money and
    // changes nothing about the discount, the offer or the delivery fee. sync-api holds the same line.
    const fixed = isShopOrder(settleOrder);
    const staffPercent = fixed ? 0 : employeeDiscountPercent;
    const discount = fixed ? settleOrder.discount : Math.min(payload.discount, settleOrder.subtotal);
    const discountSource = fixed
      ? settleOrder.discount_source
      : staffPercent > 0
      ? "employee" as const
      : payload.promoId
        ? "promo" as const
        : payload.voucherId
          ? "voucher" as const
          : payload.pointsRedeemed
            ? "points" as const
            : "none" as const;
    const discountedSub = settleOrder.subtotal - discount;
    const tax = Math.round(discountedSub * storeCfg.taxRate);
    const total = discountedSub + tax + (settleOrder.delivery_fee ?? 0);
    const earned = payload.member && !fixed ? earnPointsFor(total, discount) : 0;
    // An online order paid before it is handed over stays open: it still has stages to go through.
    const handedOver = !isShopOrder(settleOrder) || shopCharge(settleOrder, true).closes;

    // Routed through commitOrderBatch's `orderUpdate` path (routes/sales.ts's
    // `/commit` handler) instead of a plain ordersCollection.update() —
    // stock/loyalty/voucher now land atomically in the same transaction as
    // the payment-status flip, idempotent on retry via the payment_status
    // transition itself (server-side; no client-side "did this already run"
    // ref needed anymore, unlike the old settledRef).
    const commitInput = {
      orderUpdate: {
        id: settleOrder.id,
        changes: {
          payment_status: "paid" as const,
          status: handedOver ? ("closed" as const) : ("open" as const),
          // A rejected screenshot paid at the counter instead: the order goes on from here.
          ...(settleOrder.payment_review === "rejected" ? { payment_review: "approved" as const } : {}),
          tender_type: payload.tenderType,
          payment_method_name: payload.methodName,
          discount: discount,
          discount_source: discountSource,
          employee_discount_percent: staffPercent > 0 ? staffPercent : null,
          tax: tax,
          total: total,
          loyalty_member_id: fixed ? settleOrder.loyalty_member_id : (payload.member?.id ?? null),
          points_earned: fixed ? settleOrder.points_earned : earned,
          points_redeemed: fixed ? 0 : (payload.pointsRedeemed ?? 0),
          completed_at: handedOver ? now : null,
          promo_id: fixed ? settleOrder.promo_id : payload.promoId,
          voucher_id: fixed ? settleOrder.voucher_id : payload.voucherId,
        },
      },
      items: [],
      stockDeductions: computeStockDeductions(
        (settleItems ?? []).map((item) => ({
          productId: item.product_id,
          qty: item.quantity,
          optionIds: settleOptionIdsByItem.get(item.id) ?? [],
          addOns: settleAddOnsByItem.get(item.id) ?? [],
        })),
      ),
      voucherRedeem: payload.voucherId && !fixed ? { voucherId: payload.voucherId } : undefined,
      pointsRedeem: payload.member && payload.pointsRedeemed && !fixed ? { memberId: payload.member.id, points: payload.pointsRedeemed } : undefined,
      employeeDiscountPercent: staffPercent > 0 ? staffPercent : undefined,
      loyaltyEarn: payload.member && earned > 0 ? { memberId: payload.member.id, points: earned } : undefined,
    };

    // Same network-failure-queues, real-rejection-propagates split as
    // chargeNewSale — see its own comment for the reasoning. settleOrder.id
    // (not a fresh id) is the persisted-attempt key here, since this pays
    // for an order that already exists.
    let pendingSync = false;
    try {
      await commitOrderBatch(commitInput);
    } catch (err) {
      if (!(err instanceof SyncApiNetworkError)) throw err;
      await queueSale(settleOrder.id, err.requestBody);
      pendingSync = true;
    }


    setSuccess({
      orderId: settleOrder.id,
      orderNo: settleOrder.order_number,
      total: total,
      tenderLabel: payload.methodName,
      earnedPoints: earned,
      memberName: payload.member?.name ?? null,
      receiptOnScreen: payload.receiptOnScreen,
      pendingSync,
    });
    setCheckout(null);
  }

  /** Same network-failure-queues, real-rejection-propagates split
   * chargeNewSale/settleExistingOrder use — shared here since sendToKitchen
   * has three call sites (append to an open order, new dine-in, new
   * pickup) that all need it. Returns whether this attempt ended up queued
   * (pending sync) rather than actually reaching the kitchen. */
  async function commitOrQueue(input: Parameters<typeof commitOrderBatch>[0], orderId: string): Promise<boolean> {
    try {
      await commitOrderBatch(input);
      return false;
    } catch (err) {
      if (!(err instanceof SyncApiNetworkError)) throw err;
      await queueSale(orderId, err.requestBody);
      return true;
    }
  }

  async function sendToKitchen() {
    if (cart.length === 0) return;
    if (orderType === "dine_in" && !tableId) return;
    // No in-flight guard here used to mean a double-tap (or a retry after a
    // slow/dropped response — this button had no "Sending…" state at all)
    // could fire two concurrent commits, each computing the order's new
    // total from the same stale base — a real production bug, now also
    // closed server-side (routes/sales.ts recomputes the total from real
    // order_items regardless), but this is the actual fix for the
    // duplicate-submission itself, not just its symptom.
    if (sending) return;
    setSending(true);
    const now = new Date().toISOString();
    let sendPendingSync = false;

    try {
    if (orderType === "dine_in" && tableId) {
      const existing = (storeOrders ?? []).find((o) => o.table_id === tableId && o.status === "open");
      if (existing) {
        // Same open bill/tab either way (a table can keep ordering onto it
        // until it's paid) — but a fresh kitchen ticket if the latest one
        // is already ready/done, so this round doesn't get folded into an
        // already-served ticket (confusing kitchen staff, mixing done
        // items with brand-new unprepared ones). Left on the same ticket
        // when it's still new/preparing — already active/visible, no need
        // to split it into two cards for one round.
        //
        // The ticket now goes through commitOrderBatch's own `kitchenTicket`
        // field (same as the brand-new-order branches below) instead of a
        // separate pre-awaited insert — that separate insert had no conflict
        // handling at all, a real duplicate-ticket path on a retry. Built
        // once per attempt via sendAttemptRef (see its own doc), same
        // reasoning as chargeNewSale's chargeAttemptRef.
        if (!sendAttemptRef.current) {
          const hasKitchenItems = cart.some((line) => !isRetailLine(line));
          const latestTicket = hasKitchenItems ? latestTicketFor(existing.id) : null;
          const needsNewTicket = hasKitchenItems && (!latestTicket || latestTicket.status === "ready" || latestTicket.status === "done");
          const ticket = needsNewTicket ? newKitchenTicket(existing.id, now) : null;
          const ticketId = hasKitchenItems ? (ticket?.id ?? latestTicket?.id ?? null) : null;
          sendAttemptRef.current = { orderId: existing.id, ticket, ticketId, itemRows: buildOrderItemRows(existing.id, cart, now, ticketId) };
        }
        const attempt = sendAttemptRef.current;
        const newSub = existing.subtotal + subtotal;
        const newTax = Math.round(newSub * storeCfg.taxRate);
        const trimmedOrderNote = orderNote.trim();
        sendPendingSync = await commitOrQueue(
          {
            orderUpdate: {
              id: existing.id,
              changes: {
                subtotal: newSub,
                tax: newTax,
                total: newSub + newTax,
                // Same append-with-" | "-if-non-empty rule guest portal's own
                // placeOrder uses for the identical situation (adding a round
                // to an already-open order) — keeps behavior consistent
                // regardless of which side, guest or staff, added the note.
                notes: trimmedOrderNote
                  ? existing.notes
                    ? existing.notes + " | " + trimmedOrderNote
                    : trimmedOrderNote
                  : existing.notes,
              },
            },
            kitchenTicket: attempt.ticket ?? undefined,
            items: attempt.itemRows,
          },
          attempt.orderId,
        );
      } else {
        if (!sendAttemptRef.current) {
          const orderId = crypto.randomUUID();
          const ticket = cart.some((line) => !isRetailLine(line)) ? newKitchenTicket(orderId, now) : null;
          sendAttemptRef.current = { orderId, ticket, ticketId: ticket?.id ?? null, itemRows: buildOrderItemRows(orderId, cart, now, ticket?.id ?? null) };
        }
        const attempt = sendAttemptRef.current;
        sendPendingSync = await commitOrQueue(
          {
            order: {
              id: attempt.orderId,
              business_id: device.businessId,
              store_id: device.storeId,
              order_number: "AUTO",
              order_type: "dine_in",
              source: "pos",
              table_id: tableId,
              customer_name: null,
              loyalty_member_id: null,
              staff_id: staff.id,
              device_id: device.deviceId,
              subtotal: subtotal,
              discount: 0,
              discount_source: "none",
              employee_discount_percent: null,
              tax: tax,
              rounding_adjustment: 0,
              total: total,
              tender_type: null,
              payment_method_name: null,
              payment_status: "pending",
              promo_id: null,
              voucher_id: null,
              points_earned: 0,
              points_redeemed: 0,
              status: "open",
              notes: orderNote.trim() || null,
              refund_reason: null,
              refunded_at: null,
              refunded_by_staff_id: null,
              completed_at: null,
              created_at: now,
              updated_at: now,
            },
            kitchenTicket: attempt.ticket ?? undefined,
            items: attempt.itemRows,
          },
          attempt.orderId,
        );
      }
      setSentInfo({ destLabel: selectedTableLabel, pendingSync: sendPendingSync });
      // sendAttemptRef.current, not the block-scoped `attempt` above (out of
      // scope here) — guaranteed set by this point, either branch assigns
      // it unconditionally before commitOrQueue runs.
      if (!sendPendingSync && sendAttemptRef.current?.ticketId) {
        void printKitchenTicket(sendAttemptRef.current.ticketId, sendAttemptRef.current.orderId);
      }
    } else {
      if (!sendAttemptRef.current) {
        const orderId = crypto.randomUUID();
        const ticket = cart.some((line) => !isRetailLine(line)) ? newKitchenTicket(orderId, now) : null;
        sendAttemptRef.current = { orderId, ticket, ticketId: ticket?.id ?? null, itemRows: buildOrderItemRows(orderId, cart, now, ticket?.id ?? null) };
      }
      const attempt = sendAttemptRef.current;
      sendPendingSync = await commitOrQueue(
        {
          order: {
            id: attempt.orderId,
            business_id: device.businessId,
            store_id: device.storeId,
            order_number: "AUTO",
            order_type: "pickup",
            source: "pos",
            table_id: null,
            customer_name: pickupName.trim() || "Walk-in",
            loyalty_member_id: null,
            staff_id: staff.id,
            device_id: device.deviceId,
            subtotal: subtotal,
            discount: 0,
            discount_source: "none",
            employee_discount_percent: null,
            tax: tax,
            rounding_adjustment: 0,
            total: total,
            tender_type: null,
            payment_method_name: null,
            payment_status: "pending",
            promo_id: null,
            voucher_id: null,
            points_earned: 0,
            points_redeemed: 0,
            status: "open",
            notes: orderNote.trim() || null,
            refund_reason: null,
            refunded_at: null,
            refunded_by_staff_id: null,
            completed_at: null,
            created_at: now,
            updated_at: now,
          },
          kitchenTicket: attempt.ticket ?? undefined,
          items: attempt.itemRows,
        },
        attempt.orderId,
      );
      setSentInfo({ destLabel: pickupName.trim() || "Walk-in", pendingSync: sendPendingSync });
      if (!sendPendingSync && attempt.ticketId) {
        void printKitchenTicket(attempt.ticketId, attempt.orderId);
      }
    }

    setCart([]);
    finishTicket();
    setEmployeeDiscountPercent(0);
    setCartOpen(false);
    setTableId(null);
    setPickupName("");
    setOrderNote("");
    } finally {
      setSending(false);
    }
  }

  const cashSalesToday = useMemo(() => {
    // Compared as parsed Dates, not raw strings — sync serializes
    // created_at as Postgres's own text format ("2026-07-10 19:25:59.947+00"),
    // not JS's toISOString() ("...T...Z"), so a plain string >= comparison
    // against startOfTodayIso() silently always came out false.
    const todayMs = new Date(startOfTodayIso()).getTime();
    return (storeOrders ?? [])
      .filter((o) => o.tender_type === "cash" && o.payment_status === "paid" && new Date(o.created_at).getTime() >= todayMs)
      .reduce((s, o) => s + o.total, 0);
  }, [storeOrders]);

  const sendLabel = sending ? "Sending…" : orderType === "dine_in" ? (selectedTable ? `Send to ${selectedTableLabel}` : "Select a Table") : "Send to Kitchen";
  const sendDisabled = cart.length === 0 || (orderType === "dine_in" && !tableId) || sending;

  const ticketPanel = (
    <CartPanel
      isDesktop={isDesktop}
      staffName={staff.name}
      startedAt={ticketStartedAt}
      tabs={ticketTabs}
      onSwitchTab={switchTicket}
      onNewTicket={newTicket}
      onMoveTab={moveTab}
      orderType={orderType}
      onChangeOrderType={setOrderType}
      hasRestaurant={hasRestaurant}
      selectedTable={selectedTable ? { id: selectedTable.id, label: selectedTableLabel, seats: selectedTable.seats } : null}
      onChooseTable={() => {
        setCartOpen(false);
        setScreen("tables");
      }}
      pickupName={pickupName}
      onPickupName={setPickupName}
      orderNote={orderNote}
      onOrderNoteChange={setOrderNote}
      lines={cart}
      onInc={inc}
      onDec={dec}
      onRemoveLine={removeLine}
      onLineNote={setLineNote}
      onAddOnsLine={setAddOnsKey}
      onClear={clearCart}
      currency={currency}
      subtotal={subtotal}
      employeeDiscount={employeeDiscount}
      employeeDiscountPercent={employeeDiscountPercent}
      employeeDiscountAvailable={storeCfg.employeeDiscountEnabled}
      employeeDiscountRate={storeCfg.employeeDiscountPercent}
      onToggleEmployeeDiscount={toggleEmployeeDiscount}
      tax={tax}
      total={total}
      taxLabel={taxLabelFull}
      onCharge={() => setCheckout({ kind: "new" })}
      chargeDisabled={cart.length === 0 || (orderType === "dine_in" && !tableId)}
      onSend={sendToKitchen}
      sendDisabled={sendDisabled}
      sendLabel={sendLabel}
      lastOrder={lastSale && lastSale.label ? { label: lastSale.label, summary: lastSale.cart.map((l) => (l.qty > 1 ? `${l.qty} ${l.name}` : l.name)).join(", ") } : null}
      onRepeatLast={repeatLastSale}
    />
  );

  const mainContent = (
    <div
      className="no-scrollbar"
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        // Sell lays out its own padding (menu + ticket); other screens get the sheet's.
        padding: OWN_LAYOUT.has(screen) ? 0 : isDesktop ? "26px 30px calc(26px + env(safe-area-inset-bottom))" : "18px 16px calc(28px + env(safe-area-inset-bottom))",
        overflowY: OWN_LAYOUT.has(screen) ? "hidden" : "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Suspense fallback={null}>
      {screen === "home" && (
        <MenuScreen
          storeId={device.storeId}
          isDesktop={isDesktop}
          currency={currency}
          showImages={prefs.menuPhotos}
          cartQtyByProduct={cartQtyByProduct}
          onAdd={handleProductTap}
          onRequestToggleAvailability={requestToggleAvailability}
          onOpenCameraScanner={hasRetail ? () => void openCameraScanner() : undefined}
        />
      )}
      {screen === "tables" && hasRestaurant && (
        <TablesScreen
          storeId={device.storeId}
          staffId={staff.id}
          isDesktop={isDesktop}
          currency={currency}
          taxLabel={taxLabelFull}
          guestPortalEnabled={storeCfg.store?.guest_portal_enabled ?? false}
          onSeatTable={(id) => {
            setOrderType("dine_in");
            setTableId(id);
            setScreen("home");
            setCartOpen(true);
          }}
          onAddItems={(id) => {
            setOrderType("dine_in");
            setTableId(id);
            setScreen("home");
            setCartOpen(true);
          }}
          onChargeOrder={(orderId) => setCheckout({ kind: "settle", orderId })}
        />
      )}
      {screen === "kitchen" && hasRestaurant && (
        <KitchenScreen storeId={device.storeId} isDesktop={isDesktop} currency={currency} justReadyIds={justReadyIds} />
      )}
      {screen === "bills" && (
        <BillsScreen
          storeId={device.storeId}
          isDesktop={isDesktop}
          currency={currency}
          taxLabel={taxLabelFull}
          hasRetail={hasRetail}
          focusOrderId={billFocus}
          drawer={drawer}
          cashSales={cashSalesToday}
          onCloseDrawer={(result) => {
            const record = { closedAt: nowClock(), float: result.float, cashSales: cashSalesToday, counted: result.counted, variance: result.variance };
            setDrawer(record);
            writeDrawer(record);
            // The synced copy is what the owner sees under Bills; the local one drives this till's screen.
            const now = new Date().toISOString();
            void insertRow("drawer_closes", {
              id: crypto.randomUUID(),
              store_id: device.storeId,
              device_id: device.deviceId,
              staff_id: staff.id,
              opening_float: result.float,
              cash_sales: cashSalesToday,
              counted: result.counted,
              variance: result.variance,
              closed_at: now,
              created_at: now,
              updated_at: now,
            });
          }}
          onReopenDrawer={() => {
            setDrawer(null);
            writeDrawer(null);
          }}
          onChargeOrder={(orderId) => setCheckout({ kind: "settle", orderId })}
        />
      )}
      {screen === "inventory" && (hasInventory || hasRestaurant) && (
        <InventoryScreen storeId={device.storeId} staffId={staff.id} isDesktop={isDesktop} hasInventory={hasInventory} hasRestaurant={hasRestaurant} />
      )}
      {screen === "settings" && (
        <SettingsScreen
          isDesktop={isDesktop}
          staff={staff}
          storeId={device.storeId}
          storeName={storeCfg.name}
          receiptMode={storeCfg.receiptMode}
          registerName={device.registerName}
          installedModules={installedModules}
          prefs={prefs}
          onSetPref={setToggle}
          notifStatus={notifStatus}
          onEnableNotif={enableNotif}
          onSignOut={() => setConfirmSignOut(true)}
          onUnpairDevice={onUnpairDevice}
          clockedIn={clockedIn}
          clockingIn={clockingIn}
          onClockIn={onClockIn}
          onClockOut={onClockOut}
          shiftError={shiftError}
        />
      )}
      </Suspense>
    </div>
  );

  return (
    <div className={`cq-full-height pos-viewport pos-shell${isDesktop ? "" : " phone"}`} style={{ width: "100%" }}>
      <div className="pos-ghosts" aria-hidden="true">
        <i />
        <i />
        <i />
      </div>
      {scanNotice && (
        <div role={scanNotice.error ? "alert" : "status"} className={`pos-toast${scanNotice.error ? " bad" : ""}`}>
          <span className="ms" aria-hidden="true">{scanNotice.error ? "error" : "barcode_scanner"}</span>
          <span>{scanNotice.message}</span>
        </div>
      )}
      {(pendingSalesCount > 0 || attentionSalesCount > 0) && screen !== "bills" && (
        <button className={`pos-sync-pill${attentionSalesCount > 0 ? " bad" : ""}`} onClick={() => setScreen("bills")}>
          <span className="ms" aria-hidden="true">{attentionSalesCount > 0 ? "error" : "cloud_off"}</span>
          {attentionSalesCount > 0
            ? `${attentionSalesCount} ${attentionSalesCount === 1 ? "sale needs" : "sales need"} a manager`
            : `${pendingSalesCount} ${pendingSalesCount === 1 ? "sale" : "sales"} waiting to sync`}
        </button>
      )}
      {isDesktop && (
        <NavRail
          screen={screen}
          installedModules={installedModules}
          onNavigate={navigate}
          staffName={staff.name}
          onStaffMenu={() => setConfirmSignOut(true)}
          notifCount={notifications.length}
          onOpenNotif={() => setNotifOpen((v) => !v)}
        />
      )}
      {!isDesktop && (
        <MobileTopBar
          title={success ? "Paid" : checkout ? "Charge" : NAV_LABEL[screen]}
          staffName={staff.name}
          onOpenNav={() => setNavOpen(true)}
          onStaffMenu={() => setConfirmSignOut(true)}
          notifCount={notifications.length}
          onOpenNotif={() => setNotifOpen((v) => !v)}
        />
      )}
      {!isDesktop && <NavDrawer open={navOpen} screen={screen} installedModules={installedModules} storeName={storeCfg.name} onNavigate={(s) => { navigate(s); setNavOpen(false); }} onClose={() => setNavOpen(false)} />}
      <NotificationPanel
        open={notifOpen}
        isDesktop={isDesktop}
        notifications={notifications}
        onClose={() => setNotifOpen(false)}
        onDismiss={dismissNotification}
        onClearAll={clearAllNotifications}
      />

      <main className="pos-sheet">
        {success ? (
          <DoneScreen
            isDesktop={isDesktop}
            orderId={success.orderId}
            orderNo={success.orderNo}
            total={success.total}
            tenderLabel={success.tenderLabel}
            earnedPoints={success.earnedPoints}
            memberName={success.memberName}
            receiptOnScreen={!!success.receiptOnScreen}
            pendingSync={!!success.pendingSync}
            currency={currency}
            receiptMode={storeCfg.paperMode}
            onNewOrder={() => setSuccess(null)}
          />
        ) : checkout?.kind === "new" ? (
          <ChargeScreen
            isDesktop={isDesktop}
            storeId={device.storeId}
            title={ticketTabs.find((t) => t.active)?.label ?? ""}
            subtitle={`${orderTypeLabel(orderType)} · ${ticketStartedAt}`}
            subtotal={subtotal}
            employeeDiscountPercent={employeeDiscountPercent}
            parentTax={rawTax}
            taxLabel={taxLabelFull}
            currency={currency}
            hasCrm={hasCrm}
            loyaltyEarnRate={loyaltyEarnRate}
            earnOnDiscounted={earnOnDiscounted}
            redeemRate={redeemRate}
            lines={cart.map((l) => ({ name: l.name, unitPrice: l.unitPrice, qty: l.qty }))}
            onBack={() => setCheckout(null)}
            onConfirm={chargeNewSale}
          />
        ) : checkout?.kind === "settle" && settleOrder ? (
          <ChargeScreen
            isDesktop={isDesktop}
            storeId={device.storeId}
            title={`#${settleOrder.order_number}`}
            subtitle={`${orderTypeLabel(settleOrder.order_type)} · ${new Date(settleOrder.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`}
            subtotal={settleOrder.subtotal}
            parentTax={settleOrder.tax}
            taxLabel={taxLabelFull}
            currency={currency}
            hasCrm={hasCrm}
            loyaltyEarnRate={loyaltyEarnRate}
            earnOnDiscounted={earnOnDiscounted}
            redeemRate={redeemRate}
            lines={(settleItems ?? []).map((l) => ({ name: l.product_name, unitPrice: l.unit_price, qty: l.quantity }))}
            fixed={isShopOrder(settleOrder) ? { discount: settleOrder.discount, fee: settleOrder.delivery_fee ?? 0 } : undefined}
            onBack={() => setCheckout(null)}
            onConfirm={settleExistingOrder}
          />
        ) : (
          mainContent
        )}

        {!success && !checkout && screen === "home" && isDesktop && ticketPanel}
      </main>

      {screen === "home" && !isDesktop && !cartOpen && !checkout && !success && (
        <button className="pos-ticket-bar" onClick={() => setCartOpen(true)}>
          <span className="pos-line-qty num">{itemCount}</span>
          <span>
            <small>Ticket</small>
            <b className="num">{cart.length ? formatMoney(total, { currencyCode: currency.code, precision: currency.precision }) : "Nothing yet"}</b>
          </span>
          <span className="pos-ticket-bar-go">
            {cart.length ? "Review" : "Open"}
            <span className="ms" aria-hidden="true">arrow_forward</span>
          </span>
        </button>
      )}

      {screen === "home" && !isDesktop && cartOpen && !checkout && !success && (
        <>
          <div className="pos-scrim" onClick={() => setCartOpen(false)} />
          <div className="pos-ticket-pop" role="dialog" aria-modal="true" aria-label="Ticket">
            <button className="pos-ticket-close" aria-label="Back to menu" onClick={() => setCartOpen(false)}>
              <span className="ms" aria-hidden="true">expand_more</span>
            </button>
            {ticketPanel}
          </div>
        </>
      )}


      {customizing && (
        <CustomizerModal
          product={customizing}
          currency={currency}
          onClose={() => setCustomizing(null)}
          onConfirm={(qty, selections, unitPrice, note) => {
            addConfiguredProduct(customizing, qty, selections, unitPrice, note);
            setCustomizing(null);
          }}
        />
      )}


      {retailVariantProduct && (
        <RetailVariantModal
          product={retailVariantProduct}
          variants={(variantsResult?.rows ?? []).filter((variant) => variant.product_id === retailVariantProduct.id)}
          inventory={(retailInventoryResult?.rows ?? []).filter((row) => row.product_id === retailVariantProduct.id)}
          currency={currency}
          onClose={() => setRetailVariantProduct(null)}
          onPick={(variant) => {
            addScannedProduct(retailVariantProduct, variant);
            setRetailVariantProduct(null);
          }}
        />
      )}

      {cameraScanner && <CameraBarcodeModal stream={cameraScanner.stream} initialError={cameraScanner.error} onDetected={handleBarcodeScan} onClose={closeCameraScanner} />}

      {addOnsKey && (() => {
        const line = cart.find((l) => l.key === addOnsKey);
        if (!line) return null;
        const categoryId = productCategoryResult?.rows.find((p) => p.id === line.productId)?.category_id ?? null;
        const applicable = (addOnsResult?.rows ?? []).filter((a) =>
          a.order_types.includes(orderType) &&
          (a.product_ids.length === 0 || a.product_ids.includes(line.productId)) &&
          (a.category_ids.length === 0 || (categoryId != null && a.category_ids.includes(categoryId))),
        );
        return <LineAddOnsModal lineName={line.name} addOns={applicable} initial={line.selections ?? []} currency={currency} onClose={() => setAddOnsKey(null)} onSave={(selections) => { setLineAddOns(addOnsKey, selections); setAddOnsKey(null); }} />;
      })()}


      {sentInfo && (
        <div className="pos-dim" onClick={() => setSentInfo(null)}>
          <section role="dialog" aria-modal="true" aria-labelledby="sent-t" className="pos-dialog pos-sent" onClick={(e) => e.stopPropagation()}>
            <span className={`pos-sent-badge${sentInfo.pendingSync ? " wait" : ""}`}>
              <span className="ms" aria-hidden="true">{sentInfo.pendingSync ? "sync" : "soup_kitchen"}</span>
            </span>
            <h2 id="sent-t">{sentInfo.pendingSync ? "Saved on this register" : "Sent to the kitchen"}</h2>
            <p>
              {sentInfo.pendingSync
                ? "Couldn't reach the server. It goes to the kitchen by itself once you're back online. Tell the kitchen directly if it's urgent."
                : `${sentInfo.destLabel} is on the kitchen screen now.`}
            </p>
            <div className="dlg-row">
              <button
                className="pos-btn-ghost"
                onClick={() => {
                  setSentInfo(null);
                  setScreen("kitchen");
                }}
              >
                See kitchen
              </button>
              <button className="pos-btn-dark" onClick={() => setSentInfo(null)}>
                New order
              </button>
            </div>
          </section>
        </div>
      )}

      {approval && <PinApprovalModal storeId={device.storeId} label={approval.label} onCancel={() => setApproval(null)} onApproved={() => approval.onApprove()} />}

      {confirmSignOut && (
        <div className="pos-dim" onClick={() => setConfirmSignOut(false)}>
          <section role="dialog" aria-modal="true" aria-labelledby="me-t" className="pos-dialog pos-me" onClick={(e) => e.stopPropagation()}>
            <h2 id="me-t">{staff.name}</h2>
            <p>{clockedIn ? "On shift. Parked tickets stay on this register." : "Not clocked in. Parked tickets stay on this register."}</p>
            <button
              className="pos-me-main"
              onClick={() => {
                setConfirmSignOut(false);
                onSignOut({ clockOut: false });
              }}
            >
              <span className="ms" aria-hidden="true">swap_horiz</span>
              <span>Switch staff</span>
              {clockedIn && <small>Stay clocked in</small>}
            </button>
            <button
              className="pos-me-out"
              onClick={() => {
                setConfirmSignOut(false);
                onSignOut();
              }}
            >
              <span className="ms" aria-hidden="true">timer_off</span>
              <span>{clockedIn ? "Clock out and sign out" : "Sign out"}</span>
            </button>
            <button className="pos-me-cancel" onClick={() => setConfirmSignOut(false)}>
              Cancel
            </button>
          </section>
        </div>
      )}
    </div>
  );
}
